import { useState, useEffect, useRef } from 'react';
import { fetchProducts } from '../api/fetchProducts';
import { MAGAZINE_ELEC_PARAMS } from './useProductModels';

export const useProducts = (productType, pageIndex, pageSize, filters, sortOrder, search, enabled = true) => {
  const [products, setProducts] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // Guard "latest-wins": kolejne wywołania mogą wracać w innej kolejności niż wyszły
  // (np. niefiltrowane 25 ofert wolniej niż odfiltrowany 1 wynik). Bez tego starsza,
  // wolniejsza odpowiedź nadpisuje świeższy wynik filtra.
  const latestReq = useRef(0);

  const fetchFilteredProducts = async () => {
    const reqId = ++latestReq.current;
    setIsLoading(true);
    const offset = (pageIndex - 1) * pageSize;
    const queryParams = new URLSearchParams({
      limit: pageSize,
      offset,
    });

    if (search) {
      if (productType === 'panel') {
        queryParams.append('search', search);
      } else {
        queryParams.append('name', search);
      }
    }

    if (sortOrder === 'availability') {
      queryParams.append('order_by', 'availability');
    } else if (sortOrder) {
      queryParams.append('order_by_price', sortOrder);
    }

    if (filters.available_only) {
      queryParams.append('available_only', 'true');
    }

    // „Tylko jawne ceny" — chowa oferty B2B z ceną widoczną po zalogowaniu w sklepie.
    if (filters.public_price_only) {
      queryParams.append('public_price_only', 'true');
    }

    if (filters.price) {
      if (productType === 'panel') {
        queryParams.append('min_price', filters.price[0]);
        queryParams.append('max_price', filters.price[1]);
      } else {
        queryParams.append('minPrice', filters.price[0]);
        queryParams.append('maxPrice', filters.price[1]);
      }
    }

    if (filters.power) {
      if (productType === 'panel') {
        queryParams.append('min_power', filters.power[0]);
        queryParams.append('max_power', filters.power[1]);
      } else {
        queryParams.append('minPower', filters.power[0]);
        queryParams.append('maxPower', filters.power[1]);
      }
    }

    if (filters.capacity) {
      queryParams.append('minCapacity', filters.capacity[0]);
      queryParams.append('maxCapacity', filters.capacity[1]);
    }

    if (filters.manufacturer?.length > 0) {
      filters.manufacturer.forEach((manufacturer) => {
        queryParams.append('manufacturers', manufacturer);
      });
    }

    if (filters.types?.length > 0) {
      filters.types.forEach((type) => {
        queryParams.append('types', type);
      });
    }

    if (filters.voltage?.length > 0) {
      filters.voltage.forEach((voltage) => {
        queryParams.append('voltage', voltage);
      });
    }

    if (filters.phase?.length > 0) {
      filters.phase.forEach((phase) => {
        queryParams.append('phase', phase);
      });
    }

    if (productType === 'panel') {
      if (filters.frame_colour) {
        queryParams.append('frame_colour', filters.frame_colour);
      }
      if (filters.min_efficiency != null && filters.min_efficiency !== '') {
        queryParams.append('min_efficiency', filters.min_efficiency);
      }
      if (filters.min_product_warranty != null && filters.min_product_warranty !== '') {
        queryParams.append('min_product_warranty', filters.min_product_warranty);
      }
      if (filters.module_length_min != null && filters.module_length_min !== '') {
        queryParams.append('module_length_min', filters.module_length_min);
      }
      if (filters.module_length_max != null && filters.module_length_max !== '') {
        queryParams.append('module_length_max', filters.module_length_max);
      }
      if (filters.module_width_range) {
        queryParams.append('module_width_range', filters.module_width_range);
      }
      if (filters.module_thickness_range) {
        queryParams.append('module_thickness_range', filters.module_thickness_range);
      }
      if (filters.min_weight != null && filters.min_weight !== '') {
        queryParams.append('min_weight', filters.min_weight);
      }
      if (filters.max_weight != null && filters.max_weight !== '') {
        queryParams.append('max_weight', filters.max_weight);
      }
      if (filters.min_price_per_wp != null && filters.min_price_per_wp !== '') {
        queryParams.append('min_price_per_wp', filters.min_price_per_wp);
      }
      if (filters.max_price_per_wp != null && filters.max_price_per_wp !== '') {
        queryParams.append('max_price_per_wp', filters.max_price_per_wp);
      }
      // Parametry elektryczne (z kart katalogowych; filtr idzie przez warstwę modeli)
      ['min_vmp', 'max_vmp', 'min_imp', 'max_imp', 'min_voc', 'max_voc', 'min_isc', 'max_isc',
        'min_gamma_pmax', 'min_beta_voc'].forEach((p) => {
        if (filters[p] != null && filters[p] !== '') queryParams.append(p, filters[p]);
      });
      if (filters.cells_series?.length > 0) {
        filters.cells_series.forEach((c) => queryParams.append('cells_series', c));
      }
      if (filters.seller?.length > 0) {
        filters.seller.forEach((seller) => {
          queryParams.append('sellers', seller);
        });
      }
    }

    if (productType === 'inwerter') {
      if (filters.seller?.length > 0) {
        filters.seller.forEach((seller) => {
          queryParams.append('sellers', seller);
        });
      }
      // Liczba MPPT (naprawa martwego checkboxa), gwarancja i parametry elektryczne.
      if (filters.mppt?.length > 0) {
        filters.mppt.forEach((m) => queryParams.append('mppts', m));
      }
      ['min_warranty',
        'min_ac_current', 'max_ac_current', 'min_mppt_current', 'max_mppt_current',
        'min_mppt_isc', 'max_mppt_isc', 'min_dc_voltage', 'max_dc_voltage',
        'min_eff_max', 'min_eff_euro'].forEach((p) => {
        if (filters[p] != null && filters[p] !== '') queryParams.append(p, filters[p]);
      });
    }

    if (productType === 'magazyn') {
      if (filters.seller?.length > 0) {
        filters.seller.forEach((seller) => {
          queryParams.append('sellers', seller);
        });
      }
      // Parametry elektryczne z kart — muszą jechać także tutaj, inaczej ten sam
      // filtr działałby w widoku modeli i milczał w widoku ofert.
      MAGAZINE_ELEC_PARAMS.forEach((p) => {
        if (filters[p] != null && filters[p] !== '') queryParams.append(p, filters[p]);
      });
      filters.ip_ratings?.forEach((v) => queryParams.append('ip_ratings', v));
      filters.charge_temp_class?.forEach((v) => queryParams.append('charge_temp_class', v));
    }

    if (productType === 'wallbox') {
      // Katalog domyślnie pokazuje wallboxy AC; stacje komercyjne dokłada przełącznik.
      if (filters.komercyjne?.length) queryParams.append('komercyjne', 'true');
      // Nazwy parametrów muszą zgadzać się z WallboxesFilterSchema po stronie API.
      filters.manufacturer?.forEach((v) => queryParams.append('manufacturers', v));
      filters.seller?.forEach((v) => queryParams.append('sellers', v));
      filters.power_kw?.forEach((v) => queryParams.append('power_kw', v));
      filters.phases?.forEach((v) => queryParams.append('phases', v));
      filters.connector_types?.forEach((v) => queryParams.append('connector_types', v));
      filters.ip_ratings?.forEach((v) => queryParams.append('ip_ratings', v));
      filters.pv_modes?.forEach((v) => queryParams.append('pv_modes', v));
      if (filters.min_price != null && filters.min_price !== '') queryParams.append('min_price', filters.min_price);
      if (filters.max_price != null && filters.max_price !== '') queryParams.append('max_price', filters.max_price);
    }

    const endpoint = productType === 'panel'
    ? 'panels/'
    : productType === 'inwerter'
    ? 'inverters/'
    : productType === 'magazyn'
    ? 'magazines/'
    : productType === 'wallbox'
    ? 'wallboxes/'
    : null;
    
    const url = `${endpoint}?${queryParams}`;

    try {
      const fetchedData = await fetchProducts(url);
      if (reqId !== latestReq.current) return; // przyszła starsza odpowiedź — odrzuć
      setProducts(fetchedData?.items || []);
      setTotalCount(fetchedData?.count ?? 0);
      setTotalPages(Math.ceil((fetchedData?.count || 0) / pageSize));
    } catch (err) {
      if (reqId !== latestReq.current) return;
      console.error('Error fetching products', err);
      setProducts([]);
      setTotalCount(null);
    } finally {
      if (reqId === latestReq.current) setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!enabled) return;
    fetchFilteredProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productType, pageIndex, filters, sortOrder, search, enabled]);

  return {
    products,
    totalPages,
    totalCount,
    isLoading,
  };
};
